/**
 * Returns a comparable timestamp for a record object with recordDate or yearEnd fields.
 * Used for sorting records by date.
 * @param {object} record - The record object with recordInfo containing recordDate and/or yearEnd.
 * @returns {number} - Timestamp for comparison, or -Infinity if not available.
 */
export function getComparableRecordDate(record: {
  recordInfo: { recordDate?: string; yearEnd?: string };
}): number {
  const { recordDate, yearEnd } = record.recordInfo;
  if (recordDate) {
    const date = new Date(recordDate);
    if (!isNaN(date.getTime())) return date.getTime();
  }
  if (yearEnd) {
    // Use December 31st of yearEnd for comparison
    return new Date(Number(yearEnd), 11, 31).getTime();
  }
  // If neither is present, treat as the oldest possible
  return -Infinity;
}

/**
 * Parses a date string in the format YYYY-MM-DD, YYYY-MM, or YYYY into an object.
 * @param {string} date - The date string.
 * @returns {{ year: number, month: number, day: number }}
 */
export function parseDate(date: string): {
  year: number;
  month: number;
  day: number;
} {
  const parts = date.split("-").map(Number);
  if (parts.length === 3) {
    return { year: parts[0], month: parts[1], day: parts[2] };
  }
  if (parts.length === 2) {
    return { year: parts[0], month: parts[1], day: 1 };
  }
  if (parts.length === 1 && !isNaN(parts[0])) {
    return { year: parts[0], month: 1, day: 1 };
  }
  return { year: 0, month: 0, day: 1 };
}

/**
 * Returns the timestamp for the next Sunday at 2AM from now.
 * @returns {number} - Timestamp in ms for next Sunday at 2AM.
 */
export function getNextSunday2AM(): number {
  const now = new Date();
  const nextSunday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + ((7 - now.getDay()) % 7), // Calculates days until next Sunday
    2,
    0,
    0 // 2 AM in 24-hour format
  );

  // If it's currently Sunday and the time is past 2 AM, add 7 days to nextSunday
  if (now.getDay() === 0 && now.getHours() >= 2) {
    nextSunday.setDate(nextSunday.getDate() + 7);
  }

  // If the calculated time is in the past, add 7 more days
  if (nextSunday.getTime() <= now.getTime()) {
    nextSunday.setDate(nextSunday.getDate() + 7);
  }

  return nextSunday.getTime();
}
